/* global window, AbortController, Image */
import { Controller } from '@hotwired/stimulus'
import mapboxgl from 'mapbox-gl'
import { debounce } from '../utils/debounce'

const CHUNK_SIZE = 25
const DEFAULT_MAP_ZOOM = 9
const IMH_PIN_ICON = 'imh_pin'

export default class extends Controller {
  static targets = [
    'hubTypeSelect',
    'searchInput',
    'toggle',
    'sideBar',
    'locationList',
    'resultsCount'
  ]

  static values = {
    targetId: String,
    fetchUrl: String,
    pinIconUrl: String
  }

  connect () {
    this.pageNumber = 0
    this.searchParams = {}
    this.locationsList = []
    this.mapLoaded = false
    this.mapUpdating = false
    this.mapRecentering = false
    this.fetchRequestId = 0
    this.abortController = null

    this.buildSearchParams()
    this.initMap()
    this.renderMap()
    this.debouncedScroll = debounce(this.scrolled.bind(this), 200)
    this.debouncedSearch = debounce(this.applyFilters.bind(this), 250)
    this.sideBarTarget.addEventListener('scroll', this.debouncedScroll)
  }

  disconnect () {
    if (this.debouncedScroll) {
      this.sideBarTarget.removeEventListener('scroll', this.debouncedScroll)
    }

    if (this.abortController) {
      this.abortController.abort()
    }

    if (this.map) {
      this.map.remove()
    }
  }

  // Immediate apply for hub type / toggles — avoids lag and dropped updates.
  applyFilters () {
    this.buildSearchParams()
    this.updateMap()
  }

  // Debounced apply for search typing only.
  updateFilters () {
    this.debouncedSearch()
  }

  buildSearchParams () {
    const params = {}
    const hubType = this.hasHubTypeSelectTarget ? this.hubTypeSelectTarget.value : ''
    const query = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ''

    if (hubType && hubType !== 'all') {
      params.hub_type = hubType
    }

    if (query) {
      params.search_attributes = query
    }

    this.toggleTargets.forEach(target => {
      const key = target.dataset.toggleKey
      if (key) {
        params[key] = target.checked
      }
    })

    this.searchParams = params
  }

  initMap () {
    mapboxgl.accessToken = window.mapbox_data.access_token
    this.mapData = window.mapbox_data.map_data
    this.map = new mapboxgl.Map({
      container: this.targetIdValue,
      style: 'mapbox://styles/mapbox/outdoors-v12',
      center: window.mapbox_data.coordinates,
      zoom: DEFAULT_MAP_ZOOM,
      hash: true
    })
    this.map.addControl(new mapboxgl.NavigationControl(), 'bottom-right')
    this.map.on('load', () => {
      this.mapLoaded = true
      this.loadMapSource()
    })
    this.map.on('moveend', () => {
      if (this.mapUpdating || this.mapRecentering) return
      this.updateMap()
    })
  }

  async renderMap () {
    const requestId = ++this.fetchRequestId
    this.mapUpdating = true

    try {
      const res = await this.fetchLocations()
      if (requestId !== this.fetchRequestId || res == null) return

      this.locationsList = res.locations || []
      this.updateResultsCount(this.locationsList.length)
      this.loadLocations(this.locationsChunk())
      this.loadOrUpdateMapSource()
    } finally {
      if (requestId === this.fetchRequestId) {
        this.mapUpdating = false
      }
    }
  }

  async updateMap () {
    const requestId = ++this.fetchRequestId
    this.mapUpdating = true

    try {
      const res = await this.fetchLocations()
      // A newer filter/map update superseded this request.
      if (requestId !== this.fetchRequestId || res == null) return

      this.locationsList = res.locations || []
      this.updateResultsCount(this.locationsList.length)
      this.loadLocations(this.locationsChunk())
      this.loadOrUpdateMapSource()

      if (this.searchParams.search_attributes && this.locationsList.length > 0) {
        this.recenterMapToLocations()
      }
    } finally {
      if (requestId === this.fetchRequestId) {
        this.mapUpdating = false
      }
    }
  }

  scrolled () {
    const { bottom } = this.locationListTarget.getBoundingClientRect()

    if (this.sideBarTarget.clientHeight + this.sideBarTarget.offsetTop > bottom) {
      this.pageNumber += 1
      this.loadLocations(this.locationsChunk(), true)
    }
  }

  bounds () {
    const {
      _ne: { lat: latitude1, lng: longitude1 },
      _sw: { lat: latitude2, lng: longitude2 }
    } = this.map.getBounds()

    return {
      coordinates: true,
      latitude_1: latitude1,
      longitude_1: longitude1,
      latitude_2: latitude2,
      longitude_2: longitude2
    }
  }

  async fetchLocations () {
    if (this.abortController) {
      this.abortController.abort()
    }
    this.abortController = new AbortController()

    this.sideBarTarget.scrollTo(0, 0)
    const params = { ...this.searchParams }

    if (!params.search_attributes || params.search_attributes.trim() === '') {
      Object.assign(params, this.bounds())
    }

    try {
      const response = await fetch(`${this.fetchUrlValue}?${new URLSearchParams(params)}`, {
        headers: { Accept: 'application/json' },
        signal: this.abortController.signal
      })
      return response.json()
    } catch (error) {
      if (error.name === 'AbortError') return null
      throw error
    }
  }

  locationsChunk () {
    const initialValue = this.pageNumber * CHUNK_SIZE
    return this.locationsList.slice(initialValue, initialValue + CHUNK_SIZE)
  }

  loadLocations (locations, append = false) {
    if (!append) {
      this.pageNumber = 0
      this.locationListTarget.innerHTML = ''
    }

    if (this.locationsList.length === 0) {
      this.locationListTarget.innerHTML = '<div class="p-4 text-sm text-secondary">No IMH locations found.</div>'
      return
    }

    locations.forEach(location => {
      const cardElement = document.createElement('div')
      cardElement.innerHTML = window.locationCardTemplates.imh(location)
      this.locationListTarget.appendChild(cardElement)
    })
  }

  updateResultsCount (count) {
    this.resultsCountTarget.textContent = `${count} result${count !== 1 ? 's' : ''}`
  }

  loadOrUpdateMapSource () {
    if (this.map.getSource('imhMapData')) {
      this.updateMapSource()
      return
    }

    this.loadMapSource()
  }

  async loadMapSource () {
    if (!this.mapLoaded || this.map.getSource('imhMapData')) return

    await this.loadMapImages()
    this.map.addSource('imhMapData', {
      type: 'geojson',
      data: this.createFeatureCollection(),
      cluster: true,
      clusterMaxZoom: 18,
      clusterRadius: 50,
      clusterProperties: this.mapData.cluster_properties
    })
    this.addMapLayers()
    this.addMapEvents()
  }

  updateMapSource () {
    const source = this.map.getSource('imhMapData')
    if (source) {
      source.setData(this.createFeatureCollection())
    }
  }

  addMapLayers () {
    this.map.addLayer({
      id: 'imh-clusters',
      type: 'symbol',
      source: 'imhMapData',
      filter: ['has', 'point_count'],
      layout: {
        'icon-image': this.mapData.cluster_conditions,
        'icon-size': this.mapData.map_icon_size,
        'icon-allow-overlap': true
      }
    })

    this.map.addLayer({
      id: 'imh-clusters-count-bg',
      type: 'circle',
      source: 'imhMapData',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#51bbd6',
        'circle-radius': 8,
        'circle-translate': [-15, -15]
      }
    })

    this.map.addLayer({
      id: 'imh-cluster-count',
      type: 'symbol',
      source: 'imhMapData',
      filter: ['has', 'point_count'],
      paint: {
        'text-translate': [-15, -15]
      },
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
        'text-allow-overlap': true
      }
    })

    // Satellites use the IMH logo pin, falling back to the shared icon if it failed to load
    this.map.addLayer({
      id: 'imh-unclustered-point',
      type: 'symbol',
      source: 'imhMapData',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': [
          'case',
          ['==', ['get', 'hub_type'], 'satellite'],
          ['coalesce', ['image', IMH_PIN_ICON], ['image', ['get', 'icon_type']]],
          ['get', 'icon_type']
        ],
        'icon-size': ['case', ['==', ['get', 'hub_type'], 'satellite'], 0.75, this.mapData.map_icon_size],
        'icon-allow-overlap': true
      }
    })
  }

  addMapEvents () {
    this.map.on('click', 'imh-clusters', (event) => {
      const features = this.map.queryRenderedFeatures(event.point, { layers: ['imh-clusters'] })
      const clusterId = features[0].properties.cluster_id
      this.map.getSource('imhMapData').getClusterExpansionZoom(clusterId, (error, zoom) => {
        if (error) return

        this.map.easeTo({ center: features[0].geometry.coordinates, zoom: zoom })
      })
    })

    this.map.on('click', 'imh-unclustered-point', (event) => {
      const coordinates = event.features[0].geometry.coordinates.slice()
      const location = event.features[0].properties

      while (Math.abs(event.lngLat.lng - coordinates[0]) > 180) {
        coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360
      }

      new mapboxgl.Popup()
        .setLngLat(coordinates)
        .setHTML(window.locationPopupTemplates.imh(location))
        .addTo(this.map)
    })

    this.map.on('mouseenter', 'imh-clusters', () => { this.map.getCanvas().style.cursor = 'pointer' })
    this.map.on('mouseleave', 'imh-clusters', () => { this.map.getCanvas().style.cursor = '' })
  }

  loadMapImages () {
    const icons = [...new Map(this.mapData.map_icons.map(icon => [icon.id, icon])).values()]
    const promises = icons
      .filter(icon => !this.map.hasImage(icon.id))
      .map(icon => new Promise((resolve, reject) => {
        this.map.loadImage(icon.url, (error, image) => {
          if (error) {
            reject(error)
            return
          }

          this.map.addImage(icon.id, image)
          resolve(image)
        })
      }))
    return Promise.all([...promises, this.loadImhPinImage()])
  }

  // map.loadImage can't decode SVG, so the IMH pin goes through an <img> element.
  // A failed load is ignored; the layer falls back to the shared icon.
  loadImhPinImage () {
    if (!this.hasPinIconUrlValue || this.map.hasImage(IMH_PIN_ICON)) return Promise.resolve()

    return new Promise(resolve => {
      const image = new Image(40, 48)
      image.onload = () => {
        if (!this.map.hasImage(IMH_PIN_ICON)) {
          this.map.addImage(IMH_PIN_ICON, image, { pixelRatio: 1 })
        }
        resolve()
      }
      image.onerror = () => resolve()
      image.src = this.pinIconUrlValue
    })
  }

  createFeatureCollection () {
    return {
      type: 'FeatureCollection',
      features: this.locationsList.map(location => ({
        type: 'Feature',
        properties: location,
        geometry: {
          type: 'point',
          coordinates: [location.longitude, location.latitude]
        }
      }))
    }
  }

  recenterMapToLocations () {
    const valid = this.locationsList.filter(location => location.longitude && location.latitude)
    if (valid.length === 0) return

    if (valid.length === 1) {
      this.mapRecentering = true
      this.map.easeTo({
        center: [valid[0].longitude, valid[0].latitude],
        zoom: 14
      })
      this.map.once('moveend', () => { this.mapRecentering = false })
      return
    }

    const bounds = new mapboxgl.LngLatBounds()
    valid.forEach(location => bounds.extend([location.longitude, location.latitude]))
    this.mapRecentering = true
    this.map.fitBounds(bounds, { padding: 50 })
    this.map.once('moveend', () => { this.mapRecentering = false })
  }
}
